import { Component, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from "@angular/core";
import { CategoryServicesService } from "../../services/category-services.service";
import { ServiceRequestTypeItem } from "../../models/services/service-item";
import { Guid } from "guid-typescript";
import { NewRequestService } from "../../models/request/new-request/new-request-service";
import { RequestServicesService } from "../../services/request-services.service";
import { RequestDevicesService } from "../../services/request-devices.service";
import { forkJoin } from "rxjs";

interface ServiceTreeItem {
    id: number;
    uid: string;
    sku: string;
    name: string;
    isService: boolean;
    checked: boolean;
    items: ServiceTreeItem[];
    disabled: boolean;
    neededCount: number;
    minCount: number;
    isSelectedService: boolean;
    cost: number;
}

@Component({
    selector: 'services-hierarchical-selector',
    templateUrl: './services-hierarchical-selector.component.html',
    styleUrls: ['./services-hierarchical-selector.component.scss',
        '../../../../vendor/libs/angular2-ladda/angular2-ladda.scss',
        '../../../../vendor/libs/ngx-perfect-scrollbar/ngx-perfect-scrollbar.scss',
    ]
})
export class ServicesHierarchicalSelectorComponent implements OnInit {

    @Input() defaultRequestTypeId: number;
    @Input() defaultRequestCategoryId: number;
    @Input() height: number = 600;
    @Input() existingServices: NewRequestService[] = [];
    @Input() requestId: number;
    @Input() showAddAsAdditional: boolean = false;

    addAsAdditional: boolean = false;

    @Output() onContinue: EventEmitter<NewRequestService[]> = new EventEmitter<NewRequestService[]>();
    @Output() onCancel: EventEmitter<any> = new EventEmitter<any>();

    serviceRequestTypes: ServiceRequestTypeItem[] = [];
    treeDataAll: ServiceTreeItem[] = [];
    treeData: ServiceTreeItem[] = [];
    defaultExpandedKeys: string[] = [];
    expandedKeys: string[] = [];
    filterValue: string;
    totalDevicesAmount: number = 0;

    loading: boolean = false;
    showColorFromDisabled: boolean = false; // для уже выбранных также подчеркивать путь к ним

    constructor(
        private categoryServicesService: CategoryServicesService,
        private requestDevicesService: RequestDevicesService
    ) { }

    ngOnInit(): void {
        this.loading = true;

        const allServiceRequestTypes$ = this.categoryServicesService.getAllServiceRequestTypes();
        const totalDevicesAmount$ = this.requestDevicesService.getTotalAmount(this.requestId);

        forkJoin(([allServiceRequestTypes$])).subscribe(
            ([allServiceRequestTypes]) => {

                if (!!this.requestId) {
                    forkJoin(([totalDevicesAmount$])).subscribe(
                        ([totalDevicesAmount]) => {
                            this.totalDevicesAmount = totalDevicesAmount;
                        }
                    );
                }

                this.serviceRequestTypes = allServiceRequestTypes;
                this.serviceRequestTypes.forEach(x => x.disabled = this.existingServices.some(service => x.serviceId == service.serviceId));
                this.groupServiceRequestTypes();
                this.setDefaultExpandedKeys();
                this.loading = true;
            }
        );
    }

    groupServiceRequestTypes() {
        const uniqueRequestTypeIds = Array.from(new Set(this.serviceRequestTypes.map(request => request.requestTypeId)));

        this.treeData = uniqueRequestTypeIds.map(requestTypeId => {
            let requestCategories = this.serviceRequestTypes.filter(x => x.requestTypeId == requestTypeId);
            let uniqueRequestCategoryIds = Array.from(new Set(requestCategories.map(request => request.requestCategoryId)));

            let requestCategoryItems = uniqueRequestCategoryIds.map(requestCategoryId => {
                let servicesTreeItems = this.serviceRequestTypes
                    .filter(x => x.requestTypeId == requestTypeId && x.requestCategoryId == requestCategoryId)
                    .map(service => {
                        const existingService = this.existingServices.find(existingService => service.serviceId == existingService.serviceId);
                        const existingServiceFound = !!existingService;
                        return {
                            id: service.serviceId,
                            sku: service.sku,
                            name: service.serviceName,
                            disabled: service.disabled,
                            checked: existingServiceFound,
                            isService: true,
                            uid: Guid.create().toString(),
                            neededCount: existingServiceFound ? existingService.count : 0,
                            minCount: existingServiceFound ? existingService.count : 0,
                            isSelectedService: existingServiceFound,
                            cost: service.cost
                        } as ServiceTreeItem;
                    });

                return {
                    id: requestCategoryId,
                    name: this.getRequestCategoryName(requestCategoryId),
                    items: servicesTreeItems,
                    uid: Guid.create().toString()
                } as ServiceTreeItem;
            });

            return {
                id: requestTypeId,
                name: this.getRequestTypeName(requestTypeId),
                items: requestCategoryItems,
                uid: Guid.create().toString()
            } as ServiceTreeItem;
        });

        this.treeDataAll = this.treeData;
    }

    setDefaultExpandedKeys() {
        if (this.defaultRequestTypeId) {
            let requestTypeItem = this.treeDataAll.find(x => x.id == this.defaultRequestTypeId);
            if (requestTypeItem != null) {
                this.defaultExpandedKeys.push(requestTypeItem.uid);
            }

            if (requestTypeItem != null && this.defaultRequestCategoryId) {
                let requestTypeCategory = requestTypeItem.items.find(x => x.id == this.defaultRequestCategoryId);
                if (requestTypeCategory != null) {
                    this.defaultExpandedKeys.push(requestTypeCategory.uid);
                }
            }
        }

        this.expandedKeys = this.defaultExpandedKeys.map(x => x);
    }

    filterValueChange(value: string) {
        this.treeData = JSON.parse(JSON.stringify(this.treeDataAll));

        if (value && value.trim() != '') {
            this.treeData = this.filterTree(this.treeData, value);
        }
        else {
            this.expandedKeys = this.defaultExpandedKeys.map(x => x);
        }
    }

    filterTree(items: ServiceTreeItem[] = [], value: string): ServiceTreeItem[] {
        let resultItems: ServiceTreeItem[] = [];

        items.forEach(item => {
            let children: ServiceTreeItem[] = [];
            let nameIsMatch = this.nameIsMatch(item, value);
            let skuIsMatch = this.skuIsMatch(item, value);

            if (nameIsMatch || (!nameIsMatch && !item.isService)
                || skuIsMatch || (!nameIsMatch && !item.isService)) {
                children = this.filterTree(item.items, value);
                nameIsMatch = nameIsMatch || children.length > 0;
                skuIsMatch = skuIsMatch || children.length > 0;
            }

            if (nameIsMatch || skuIsMatch) {
                item.items = children;
                resultItems.push(item);

                if (children.length > 0 && !this.expandedKeys.includes(item.uid)) {
                    this.expandedKeys.push(item.uid);
                }
            }
        });

        return resultItems;
    }

    checkItem(dataItem: ServiceTreeItem, value: boolean) {
        this.treeDataAll.forEach(requestTypeItem => {
            requestTypeItem.items.forEach(requestCategoryItem => {
                requestCategoryItem.items.forEach(serviceItem => {
                    if (serviceItem.uid == dataItem.uid) {
                        serviceItem.checked = value;
                        serviceItem.neededCount = value ? 1 : 0
                    }
                });
            });
        });
    }

    changeNeededCount(dataItem: ServiceTreeItem, value: number) {
        this.treeDataAll.forEach(requestTypeItem => {
            requestTypeItem.items.forEach(requestCategoryItem => {
                requestCategoryItem.items.forEach(serviceItem => {
                    if (serviceItem.uid == dataItem.uid) {
                        serviceItem.checked = value !== 0;
                        serviceItem.neededCount = value;
                    }
                });
            });
        });
    }

    chooseService(dataItem: ServiceTreeItem) {
        if (!dataItem.disabled) {
            dataItem.checked = !dataItem.checked;
            this.checkItem(dataItem, dataItem.checked);
        }
    }

    expandOrCollapseGroup(dataItem: ServiceTreeItem) {
        if (this.expandedKeys.some(x => x == dataItem.uid)) {
            this.expandedKeys = this.expandedKeys.filter(x => x != dataItem.uid);
        }
        else {
            this.expandedKeys.push(dataItem.uid);
        }
    }

    saveChanges() {
        let services: NewRequestService[] = [];
        this.treeDataAll.forEach(requestTypeItem => {
            requestTypeItem.items.forEach(requestCategoryItem => {
                requestCategoryItem.items.forEach(serviceItem => {
                    if (serviceItem.checked && !serviceItem.disabled && !services.some(service => serviceItem.id == service.serviceId)) {
                        services.push({ serviceId: serviceItem.id, count: serviceItem.neededCount, cost: serviceItem.cost } as NewRequestService);
                    }
                });
            });
        });

        this.onContinue.emit(services);
    }

    cancel() {
        this.onCancel.emit();
    }

    hasChecked(dataItem: ServiceTreeItem) {
        return this.showColorFromDisabled
            ? dataItem.items.some(x => x.checked || (x.items && x.items.some(i => i.checked)))
            : dataItem.items.some(x => (x.checked && !x.disabled) || (x.items && x.items.some(i => i.checked && !i.disabled)));
    }

    getRequestTypeName(id: number): string {
        return this.serviceRequestTypes.find(x => x.requestTypeId == id).requestTypeName;
    }

    getRequestCategoryName(id: number): string {
        return this.serviceRequestTypes.find(x => x.requestCategoryId == id).requestCategoryName;
    }

    nameIsMatch(item: ServiceTreeItem, value: string): boolean {
        return item.name.toLowerCase().includes(value.toLowerCase());
    }

    private skuIsMatch(item: ServiceTreeItem, value: string): boolean {
        return !!item.sku && item.sku.toLowerCase().includes(value.toLowerCase());
    }

    get totalPrice() {
        let filteredData = [];
        this.treeDataAll.forEach(requestTypeItem => {
            requestTypeItem.items.forEach(requestCategoryItem => {
                requestCategoryItem.items.forEach(serviceItem => {
                    if (serviceItem.checked && !filteredData.map(m => m.id).includes(serviceItem.id)) {
                        filteredData.push({
                            id: serviceItem.id,
                            cost: serviceItem.cost,
                            quantity: serviceItem.neededCount == null ? 1 : serviceItem.neededCount,
                        });
                    }
                });
            });
        });

        console.log(filteredData);

        const totalPrice = filteredData.reduce((sum, item) => sum + (item.cost * item.quantity), 0);
        return totalPrice;
    }
    
    @HostListener('wheel', ['$event'])
        onWheel(e: WheelEvent) {
        // если внутри есть скроллируемый контейнер и он может скроллиться — оставляем
        // иначе блокируем, чтобы не скроллился body
        e.preventDefault();
        e.stopPropagation();
    }
}